import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import ActionButton from 'components/action-button';
import { IStoreDiagnosticData } from 'components/store-card-icons/internal-store-diagnostic-modal';
import SlimStoreCardIcons from 'components/store-card-icons/slim-store-card-icons';
import { useFavoriteStores } from 'hooks/favorite-stores';
import { useStoreCard } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { routes } from 'utils/routing';
import { fadeIn } from 'utils/style/animations';

import { useOrderButtonProps } from './hooks/use-order-button-props';
import { messages } from './store-card-slim-messages';

const StoreCardSlimContainer = styled.div<{ isSelected?: boolean }>`
  display: flex;
  padding: 0.5rem 0.75rem;
  position: relative;
  background: ${Styles.color.cardBackground};
  pointer-events: all;
  opacity: 0;
  animation: 0.2s ${fadeIn} forwards;

  ::before {
    content: '';
    height: 100%;
    border-left: 3px solid transparent;
    border-color: ${p => p.isSelected && Styles.color.primary};
    left: 0;
    top: 0;
    position: absolute;
  }
`;

const StoreCardSlimMainButton = styled.button`
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 0;
  border-radius: 0;
  cursor: pointer;
`;

const StoreCardSlimWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

const StoreInfoContainer = styled.div`
  flex: 1 1 auto;
  /* this is the "fix" for the overflow ellipses */
  min-width: 0;
`;
const StoreName = styled.h5`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.125rem;
  margin: 0;
  text-transform: ${Styles.textTransform.headlines};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StoreDistance = styled.div`
  color: ${Styles.color.grey.one};
  font-size: 0.857rem;
  line-height: 1.5rem;
`;
const StoreHoursInfo = styled(RouterLink)`
  color: ${Styles.color.grey.one};
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
  position: relative;
`;
const ClosesInText = styled.span`
  font-size: 0.875rem;
  color: ${Styles.color.error};
  margin-left: 0.5rem;
`;

const AvailableServiceModesText = styled.p`
  color: ${Styles.color.grey.one};
  font-size: 0.875rem;
  margin: 0;
`;

const StoreOrderContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

interface IStoreCardSlimViewProps {
  availableServiceModesText?: string;
  storeTitle: string;
  storeDistance?: string;
  storeDetailsRoute?: string;
  storeDetailsText: string;
  storeDetailsAriaLabel?: string;
  closesInText?: string;
  orderOrSelectButtonText: string;
  storeAriaDescribedBy?: string;
  restaurantId: string;
  storeNumber: string;
  isSelected?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onClick(): void;
  onClickStoreHoursInfo: () => void;
  orderButtonAriaLabel?: string;
  selectStoreButtonAriaLabel?: string;
  isRestaurantPosOnline?: boolean | null;
  orderButtonEnabled?: boolean;
  onOrderOrSelectClick: OnClick;
  favStoresEnabled?: boolean;
  enableOrdering: boolean;
  areStoreDiagnosticsEnabled: boolean;
  storeDiagnosticData: IStoreDiagnosticData;
  checkingItemAvailability?: boolean;
}

export const StoreCardSlimView: React.FC<IStoreCardSlimViewProps> = ({
  availableServiceModesText,
  storeTitle,
  storeDistance,
  storeDetailsRoute = '/',
  isSelected,
  storeDetailsText,
  storeDetailsAriaLabel = '',
  closesInText,
  orderOrSelectButtonText,
  innerRef,
  storeAriaDescribedBy = '',
  restaurantId,
  storeNumber = '',
  orderButtonAriaLabel = '',
  selectStoreButtonAriaLabel = '',
  isRestaurantPosOnline,
  orderButtonEnabled,
  onOrderOrSelectClick = () => {},
  onClick,
  onClickStoreHoursInfo,
  favStoresEnabled,
  enableOrdering,
  areStoreDiagnosticsEnabled,
  storeDiagnosticData,
  checkingItemAvailability,
}) => {
  const shouldStoreIconsDisplay = favStoresEnabled || areStoreDiagnosticsEnabled;
  const availabilityIsLoading = isRestaurantPosOnline === null;

  return (
    <StoreCardSlimContainer isSelected={isSelected} ref={innerRef}>
      <StoreCardSlimMainButton onClick={onClick} />
      <StoreCardSlimWrapper>
        <StoreInfoContainer>
          <StoreName>{storeTitle}</StoreName>
          {storeDistance && <StoreDistance>{storeDistance}</StoreDistance>}
          <div>
            {availableServiceModesText && (
              <AvailableServiceModesText data-testid="available-service-mode-text">
                {availableServiceModesText}
              </AvailableServiceModesText>
            )}
            <StoreHoursInfo
              aria-label={storeDetailsAriaLabel}
              aria-describedby={storeAriaDescribedBy}
              to={storeDetailsRoute}
              onClick={onClickStoreHoursInfo}
            >
              {storeDetailsText}
            </StoreHoursInfo>
            {closesInText && <ClosesInText>{closesInText}</ClosesInText>}
          </div>
        </StoreInfoContainer>
        {shouldStoreIconsDisplay && (
          <SlimStoreCardIcons
            storeNumber={storeNumber}
            storeId={restaurantId}
            diagnosticsEnabled={areStoreDiagnosticsEnabled}
            storeDiagnosticData={storeDiagnosticData}
          />
        )}
        <StoreOrderContainer>
          <ActionButton
            data-testid={enableOrdering ? 'order-here' : 'select-this-store'}
            disabled={enableOrdering ? !orderButtonEnabled : false}
            onClick={onOrderOrSelectClick}
            aria-label={enableOrdering ? orderButtonAriaLabel : selectStoreButtonAriaLabel}
            aria-describedby={storeAriaDescribedBy}
            isLoading={availabilityIsLoading || checkingItemAvailability}
            onlyIcon
          >
            {orderOrSelectButtonText}
          </ActionButton>
        </StoreOrderContainer>
      </StoreCardSlimWrapper>
    </StoreCardSlimContainer>
  );
};

export interface IStoreCardSlimProps {
  restaurant: IStore;
  isSelected?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onClick(): void;
  onClickRestaurantHoursInfo?: () => void;
}

export const StoreCardSlim: React.FC<IStoreCardSlimProps> = ({
  restaurant,
  onClick,
  innerRef,
  isSelected,
}) => {
  const {
    distanceText,
    closeHourTodayText,
    isWithinOneHourOfCloseToday,
    curbsideOpen,
    driveThruOpen,
    diningRoomOpen,
    dineInOpen,
    takeoutOpen,
    selectRestaurant,
    isRestaurantOpen,
    isMobileOrderingAvailable,
    enableOrdering,
    areDiagnosticToolsEnabled,
    checkingItemAvailability,
    isRestaurantPosOnline,
  } = useStoreCard(restaurant);

  const { formatMessage } = useIntl();
  const { favStoresEnabled } = useFavoriteStores();
  const { allServiceModesUnavailable } = useServiceModeStatus(restaurant);
  const mParticle = useMParticleContext();
  const showStoreServiceModes = useFlag(LaunchDarklyFlag.SHOW_STORE_SERVICE_MODES_SLIM_CARDS);

  const { onOrderClick, orderButtonEnabledOverride, orderButtonText } = useOrderButtonProps({
    enableOrdering,
    isMobileOrderingAvailable,
    defaultClickEventHandler: selectRestaurant,
  });

  const storeTitle = restaurant.physicalAddress?.address1 || '';

  const handleOrderClick = useCallback(
    () => onOrderClick({ ...restaurant, name: storeTitle, hasSelection: true }),
    [onOrderClick, restaurant, storeTitle]
  );

  // Originally put into 'store-card' here:
  // https://github.com/rbilabs/EngineeringMonoRepo/pull/5284/files#diff-3b3d121668a85ccc2bbc1ad39e69a799R43-R46
  // This check is possibly a check for bad data from sanity and just trying to avoid rendering?
  // TODO: figure out why this check exists?
  if (!restaurant?.number || !restaurant?.physicalAddress?.address1) {
    return null;
  }

  const storeDetailsRoute = `${routes.store}/${restaurant._id}`;
  const storeDetailsText = formatMessage(messages.hoursAndInfo);

  const orderButtonEnabled =
    isRestaurantOpen && isRestaurantPosOnline && !allServiceModesUnavailable;

  const closesInText = isWithinOneHourOfCloseToday
    ? formatMessage(messages.closesAt, { time: closeHourTodayText })
    : undefined;

  const availableServiceModes = showStoreServiceModes
    ? [
        dineInOpen && formatMessage({ id: 'dineIn' }),
        takeoutOpen && formatMessage({ id: 'takeOut' }),
        driveThruOpen && formatMessage({ id: 'driveThru' }),
        curbsideOpen && formatMessage({ id: 'curbside' }),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const orderButtonAriaLabel = formatMessage(messages.orderAtThisStore);
  const selectStoreButtonAriaLabel = formatMessage(messages.selectThisStore);
  const storeDetailsAriaLabel = formatMessage(messages.storeInfoAssistive);

  const storeDiagnosticData: IStoreDiagnosticData = {
    driveThruOpen,
    diningRoomOpen,
    isRestaurantOpen,
    isMobileOrderingAvailable,
    isRestaurantPosOnline: !!isRestaurantPosOnline,
    restaurantName: restaurant.name,
    restaurantHasMobileOrdering: restaurant.hasMobileOrdering,
    restaurantCountry: restaurant.physicalAddress.country || '',
    restaurantStreetAddress: restaurant.physicalAddress.address1 || '',
  };

  const onClickStoreHoursInfo = () => {
    mParticle.logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.Other, {
      Name: 'Store details event',
      StoreID: restaurant.number,
    });
  };

  return (
    <StoreCardSlimView
      availableServiceModesText={availableServiceModes}
      storeTitle={storeTitle}
      onClick={onClick}
      onClickStoreHoursInfo={onClickStoreHoursInfo}
      innerRef={innerRef}
      storeDistance={distanceText}
      storeDetailsRoute={storeDetailsRoute}
      storeDetailsText={storeDetailsText}
      storeDetailsAriaLabel={storeDetailsAriaLabel}
      closesInText={closesInText}
      isSelected={isSelected}
      orderOrSelectButtonText={formatMessage(orderButtonText)}
      orderButtonAriaLabel={orderButtonAriaLabel}
      selectStoreButtonAriaLabel={selectStoreButtonAriaLabel}
      storeAriaDescribedBy={restaurant.name}
      restaurantId={restaurant._id}
      storeNumber={restaurant.number}
      isRestaurantPosOnline={isRestaurantPosOnline}
      orderButtonEnabled={orderButtonEnabled || orderButtonEnabledOverride}
      onOrderOrSelectClick={handleOrderClick}
      favStoresEnabled={favStoresEnabled}
      enableOrdering={enableOrdering}
      areStoreDiagnosticsEnabled={areDiagnosticToolsEnabled}
      storeDiagnosticData={storeDiagnosticData}
      checkingItemAvailability={checkingItemAvailability}
    />
  );
};
